import { Role } from '@geartracker/types';

import { createZodDto } from './zod';

import { z } from 'zod';

// models
const FileSchema = z.object({
  type: z.enum([
    'TechnicalNotice',
    'DeclarationOfConformity',
    'PPE-inspectionProcedure',
    'PPE-checklist',
    'unknown',
  ]),
  href: z.string(),
  name: z.string().optional(),
});

const LocalizedDataSchema = z.object({
  manufacturerUrl: z.string().optional(),
  shortDescription: z.string().optional(),
  description: z.string(),
  name: z.string(),
  files: FileSchema.array().optional(),
});

const ImageSchema = z.object({
  name: z.string().optional(),
  originUrl: z.string(),
  url: z.string().optional(),
});

const PriceSchema = z.object({
  USD: z.number().optional(),
  EUR: z.number().optional(),
});

const ModelSchema = z.object({
  name: z.string(),
  manufacturerId: z.string(),
  partNumbers: z.string().nullable().array().optional(),
  images: ImageSchema.array().optional(),
  fr: LocalizedDataSchema.optional(),
  'en-us': LocalizedDataSchema.optional(),
  category: z.string().optional(),
  price: PriceSchema.optional(),
  internalCategory: z.string().optional(),
  isGeneric: z.boolean().optional(),
  supplier: z.string().optional(),
  inspectionPeriod: z.number().optional(),
  manufactureLifeSpan: z.number().optional(),
  commissioningLifeSpan: z.number().optional(),
});

const OrgCategorySchema = z.object({
  name: z.string(),
  // default values, can be overriden by model
  inspectionPeriod: z.number().optional(),
  manufactureLifeSpan: z.number().optional(),
  commissioningLifeSpan: z.number().optional(),
});

const BrandSchema = z.object({
  name: z.string(),
  slug: z.string(),
  logo: z.string().optional(),
  isCustomModels: z.boolean().optional(),
  organizationId: z.coerce.number().optional(),
  orgCategories: z.record(z.string(), OrgCategorySchema).optional(),
  models: ModelSchema.array(),
});

// equipments

const statusTransform = (val: string | null, ctx: z.RefinementCtx) => {
  if (val === null) {
    return null;
  }
  switch (val) {
    case 'Safe for use':
    case "Sûr à l'emploi":
      return 'IN_STOCK';
    case 'À réparer':
    case 'To Repair':
      return 'DAMAGED';
    case 'Manquant':
    case 'Missing':
      return 'LOST';
    case 'Jeté':
    case 'Discarded':
      return 'TRASH';
    default:
      ctx.addIssue({
        code: 'custom',
        message: 'Not a valid status',
      });
      return z.NEVER;
  }
};

export const RawEquipmentSchema = z
  .object({
    /** marque */
    A: z.string().nullable(),
    /** model */
    B: z.string().nullable(),
    /** model id */
    C: z.number().nullable(),
    /** nom eqpt */
    D: z.coerce.string(),
    /** serial */
    E: z.coerce.string().nullable(),
    /** user full name */
    F: z.coerce.string().nullable(),
    /** user email */
    G: z.coerce.string().toLowerCase().pipe(z.email()).nullable(),
    /** folder path */
    H: z.coerce.string().nullable(),
    /** description */
    I: z.coerce.string().nullable(),
    /** tags NEW */
    J: z.coerce.string().nullable(),
    /** date de fabrication */
    K: z.date().nullable(),
    /** durée de vie (années) */
    L: z.coerce.number().nullable(),
    /** date de mise en service */
    M: z.coerce.date().nullable(),
    /** durée de service maximum */
    N: z.coerce.number().nullable(),
    /** intervale de vérification */
    O: z.coerce.number().nullable(),
    /** price (cents) */
    P: z.coerce.number().nullable(),
    /**
     * Inspection 1–10
     * Date, Status, Inspector email, Comment
     */

    Q: z.coerce.date().nullable(),
    R: z.string().nullable().transform(statusTransform),
    S: z.email().nullable(),
    T: z.string().nullable(),

    U: z.coerce.date().nullable(),
    V: z.string().nullable().transform(statusTransform),
    W: z.email().nullable(),
    X: z.string().nullable(),

    Y: z.coerce.date().nullable(),
    Z: z.string().nullable().transform(statusTransform),
    AA: z.email().nullable(),
    AB: z.string().nullable(),

    AC: z.coerce.date().nullable(),
    AD: z.string().nullable().transform(statusTransform),
    AE: z.email().nullable(),
    AF: z.string().nullable(),

    AG: z.coerce.date().nullable(),
    AH: z.string().nullable().transform(statusTransform),
    AI: z.email().nullable(),
    AJ: z.string().nullable(),

    AK: z.coerce.date().nullable(),
    AL: z.string().nullable().transform(statusTransform),
    AM: z.email().nullable(),
    AN: z.string().nullable(),

    AO: z.coerce.date().nullable(),
    AP: z.string().nullable().transform(statusTransform),
    AQ: z.email().nullable(),
    AR: z.string().nullable(),

    AS: z.coerce.date().nullable(),
    AT: z.string().nullable().transform(statusTransform),
    AU: z.email().nullable(),
    AV: z.string().nullable(),

    AW: z.coerce.date().nullable(),
    AX: z.string().nullable().transform(statusTransform),
    AY: z.email().nullable(),
    AZ: z.string().nullable(),

    BA: z.coerce.date().nullable(),
    BB: z.string().nullable().transform(statusTransform),
    BC: z.email().nullable(),
    BD: z.string().nullable(),
  })
  .transform((raw) => {
    // Helper: build inspections array
    const inspections = [
      { date: raw.Q, status: raw.R, inspector: raw.S, comment: raw.T },
      { date: raw.U, status: raw.V, inspector: raw.W, comment: raw.X },
      { date: raw.Y, status: raw.Z, inspector: raw.AA, comment: raw.AB },
      { date: raw.AC, status: raw.AD, inspector: raw.AE, comment: raw.AF },
      { date: raw.AG, status: raw.AH, inspector: raw.AI, comment: raw.AJ },
      { date: raw.AK, status: raw.AL, inspector: raw.AM, comment: raw.AN },
      { date: raw.AO, status: raw.AP, inspector: raw.AQ, comment: raw.AR },
      { date: raw.AS, status: raw.AT, inspector: raw.AU, comment: raw.AV },
      { date: raw.AW, status: raw.AX, inspector: raw.AY, comment: raw.AZ },
      { date: raw.BA, status: raw.BB, inspector: raw.BC, comment: raw.BD },
    ].filter(
      (insp) =>
        insp.date !== null ||
        insp.status !== null ||
        insp.inspector !== null ||
        insp.comment !== null,
    );

    return {
      brand: raw.A,
      model: raw.B,
      modelId: raw.C,
      name: raw.D,
      serial: raw.E,
      userFullName: raw.F,
      userEmail: raw.G,
      folderPath: raw.H,
      description: raw.I,
      price: raw.P,
      tags: raw.J,
      fabricationDate: raw.K,
      fabricationLifeSpan: raw.L,
      serviceDate: raw.M,
      serviceLifeSpan: raw.N,
      inspectionInterval: raw.O,
      inspections,
    };
  });

export const rawUserSchema = z
  .object({
    /** firstName */
    A: z.coerce.string(),
    /** lastName */
    B: z.coerce.string(),
    /** name */
    C: z.coerce.string(),
    /** externalUserId */
    D: z.coerce.string().trim().nullable(),
    /** email */
    E: z.coerce.string().trim().toLowerCase().pipe(z.email()).nullable(),
    /** role */
    F: z.enum(Role).nullable(),
    /** comment */
    G: z.coerce.string().nullable(),
    /** paths */
    H: z.coerce.string().nullable(),
  })
  .transform((raw) => {
    return {
      firstName: raw.A,
      lastName: raw.B,
      name: raw.C,
      externalUserId: raw.D,
      email: raw.E,
      role: raw.F,
      comment: raw.G,
      folderPaths: raw.H
        ? raw.H.split(';')
            .map((p) => p.trim())
            .filter((p) => p.length > 0)
        : [],
    };
  });

export const rawFoldersSchema = z
  .object({
    /** folderPath */
    A: z.coerce.string().min(1),
  })
  .transform((raw) => {
    return {
      folderPath: raw.A,
    };
  });

export const ModelFilesSchema = BrandSchema.array();

// schema for a list of files successfully imported
const ImportedFilesSchema = z.object({
  href: z.string().array(),
});

export class ImportedFilesDto extends createZodDto(ImportedFilesSchema) {}
